/**
 * @file: openChatConversation.ts
 *
 * The one way to open a chat room as a full-screen surface: PUSH the
 * app-level `_chat` flow on the root stack, carrying the
 * {@link ConversationIntent} as its route params.
 *
 * A push (not a navigate): opening a second room while one is open stacks a
 * fresh flow instead of mutating the params of an already-mounted
 * conversation, which resolves its room once on mount and would ignore them.
 *
 * The route name is spelled here rather than imported from `app/Routes` so the
 * chat flow doesn't depend on the root screen map.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackActions } from '@react-navigation/native';
import { useCallback } from 'react';
import { ConversationIntent } from '../conversation/types';

type Dispatcher = { dispatch: (action: any) => void };

/** Push the chat flow from a navigation object / the global navigation ref. */
export function pushChatConversation(
  navigation: Dispatcher,
  intent: ConversationIntent,
) {
  navigation.dispatch(StackActions.push('_chat', intent));
}

/** Hook form: resolves the ROOT navigator, so it works from any nested stack. */
export const useOpenChatConversation = () => {
  const navigation = useAppNavigation();
  return useCallback(
    (intent: ConversationIntent) => pushChatConversation(navigation, intent),
    [navigation],
  );
};
